import styled from '@emotion/styled';
import { Col, Row } from 'antd';
import { getHours, getMinutes, set } from 'date-fns';
import React from 'react';

import colors from 'config/theme/colors';
import { UTC } from 'constants/Date';
import { IS_PRODUCTION } from 'constants/Global';

import DatePicker from 'ui/base/DatePicker';
import TimePicker from 'ui/base/TimePicker';
import { convertTimeZones, tryParse } from 'utils/date';

const Icon = styled.span`
  color: ${colors['@input-border-color']} !important;
`;

/**
 * @typedef {{
 *   value: string,
 *   timeFormat?: string,
 * } & import('antd/lib/date-picker').DatePickerProps
 *   & import('antd/lib/time-picker').TimePickerProps
 * } DateTimePickerProps
 */

/** @type {React.FC<DateTimePickerProps>} */
const DateTimePicker = ({ value, onDateChange, onTimeChange, timeFormat = 'HH:00', showNow, ...props }) => {
  const time = value ? tryParse(value) : undefined;

  // Picking a date cell keeps the current time (rc-picker resets it otherwise)
  const handleDateChange = React.useCallback((newDate) => {
    let parsedDate = tryParse(newDate);

    if (parsedDate) {
      const timeToUse = time || (typeof showNow === 'string' ? convertTimeZones(new Date(), UTC, showNow) : new Date());
      parsedDate = set(parsedDate, { hours: getHours(timeToUse), minutes: getMinutes(timeToUse) });
    }

    const callbackFn = onDateChange ?? props.onChange;
    callbackFn?.(parsedDate);
  }, [onDateChange, props.onChange, time, showNow]);

  return (
    <Row gutter={[12, 12]} type="flex">
      <Col xs={24} sm={14} className="date-picker">
        <DatePicker
          value={value}
          className="w-100"
          allowClear={false}
          data-cy="input-date"
          {...props}
          showNow={showNow}
          onChange={handleDateChange}
          suffixIcon={<Icon className="fb-icon--calendar" />}
        />
      </Col>
      <Col xs={24} sm={10} className="time-picker">
        <TimePicker
          value={time}
          className="w-100"
          allowClear={false}
          format={timeFormat}
          onChange={onTimeChange}
          placeholder="time_placeholder"
          id={!IS_PRODUCTION && 'time-picker'}
          suffixIcon={<Icon className="fb-icon--clock" />}
          {...props}
          showNow={false}
        />
      </Col>
    </Row>
  );
};

export default DateTimePicker;
